import { AppFeature, DocumentEntityType, DocumentExpiryFilter } from '@repo/shared'
import {
  Button,
  Card,
  CardContent,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Spinner,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@repo/ui'
import { useDebouncedValue } from '@tanstack/react-pacer'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  AlertCircle,
  FileText,
  LayoutGrid,
  Rows3,
  Search,
  Upload,
  X,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import type { SortState } from '@/components/shared/sortable-table-head'
import type { DocumentResponse } from '@repo/api'

import PageHeader from '@/components/layout/page-header'
import { ConfirmDeleteDialog } from '@/components/shared/confirm-delete-dialog'
import { ListPagination } from '@/components/shared/list-pagination'
import { sortItems, toggleSort } from '@/components/shared/sortable-table-head'
import CreateDocumentDialog from '@/features/documents/components/create-document-dialog'
import DocumentDetailsDialog from '@/features/documents/components/document-details-dialog'
import ReplaceDocumentDialog from '@/features/documents/components/replace-document-dialog'
import { DocumentGrid } from '@/features/documents/components/document-grid'
import {
  DocumentTable,
  documentSortAccessor,
} from '@/features/documents/components/document-table'
import {
  useDeleteDocument,
  useGetDocumentsPaginated,
  useGetDocumentsStats,
} from '@/features/documents/hooks'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { localizedPageTitle } from '@/lib/page-title'
import { FeatureGate } from '@/components/shared/feature-gate'

const documentsSearchSchema = z.object({
  search: z.string().optional(),
})

export const Route = createFileRoute('/dashboard/documents/')({
  head: () => ({ meta: [{ title: localizedPageTitle('documents') }] }),
  component: () => (
    <FeatureGate feature={AppFeature.DocumentsManagement}>
      <DocumentsPage />
    </FeatureGate>
  ),
  validateSearch: documentsSearchSchema,
})

type EntityTab = 'all' | DocumentEntityType
type LayoutView = 'cards' | 'table'

const EMPTY_DOCS: Array<DocumentResponse> = []

export default function DocumentsPage() {
  useDocumentTitle('documents')
  const { t } = useTranslation(['documents', 'common'])
  const [layout, setLayout] = useState<LayoutView>(() => {
    const stored = localStorage.getItem('documents-layout')
    return stored === 'table' ? 'table' : 'cards'
  })
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const searchQuery = search.search ?? ''
  const [debouncedSearch] = useDebouncedValue(searchQuery, { wait: 300 })
  const [expiryFilter, setExpiryFilter] = useState<
    'all' | DocumentExpiryFilter
  >('all')
  const [entityTab, setEntityTab] = useState<EntityTab>('all')
  const [sort, setSort] = useState<SortState>(null)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [selectedDocument, setSelectedDocument] =
    useState<DocumentResponse | null>(null)
  const [documentToReplace, setDocumentToReplace] =
    useState<DocumentResponse | null>(null)
  const [documentToDelete, setDocumentToDelete] =
    useState<DocumentResponse | null>(null)
  const {
    mutate: deleteDocument,
    variables: deletingDocVar,
    isPending,
  } = useDeleteDocument()
  const deletingDocId = isPending ? deletingDocVar : undefined

  const {
    data: paginated,
    isLoading,
    isFetching,
  } = useGetDocumentsPaginated({
    page,
    pageSize,
    search: debouncedSearch || undefined,
    expiry: expiryFilter !== 'all' ? expiryFilter : undefined,
    entityType: entityTab !== 'all' ? entityTab : undefined,
  })

  const { data: stats } = useGetDocumentsStats()

  const handleSearchChange = (value: string) => {
    navigate({
      search: (prev) => ({ ...prev, search: value || undefined }),
      replace: true,
    })
    setPage(1)
  }

  const handleExpiryChange = (value: string) => {
    setExpiryFilter(value as 'all' | DocumentExpiryFilter)
    setPage(1)
  }

  const handleTabChange = (value: string) => {
    setEntityTab(value as EntityTab)
    setPage(1)
  }

  const handlePageSizeChange = (size: number) => {
    setPageSize(size)
    setPage(1)
  }

  const handleLayoutChange = (newLayout: LayoutView) => {
    setLayout(newLayout)
    localStorage.setItem('documents-layout', newLayout)
  }

  const handleSort = (key: string) => setSort((prev) => toggleSort(prev, key))

  const items = paginated?.items ?? EMPTY_DOCS
  const sortedItems = useMemo(
    () => sortItems(items, sort, documentSortAccessor),
    [items, sort],
  )

  if (isLoading) {
    return (
      <div className="space-y-4">
        <PageHeader
          title={t('documents:title')}
          subtitle={t('documents:subtitle')}
        />
        <div className="flex flex-wrap gap-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 min-w-[200px] flex-1" />
          ))}
        </div>
        <Skeleton className="h-10 w-full" />
        <div className="flex flex-wrap gap-3">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton
              key={i}
              className="h-48 w-full sm:w-[calc(50%-6px)] lg:w-[calc(25%-9px)]"
            />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('documents:title')}
        subtitle={t('documents:subtitle')}
        ctaButton={
          <Button
            size="lg"
            className="primary-bg-gradient"
            onClick={() => setCreateDialogOpen(true)}
          >
            <Upload className="mr-2 h-4 w-4" />
            {t('documents:uploadDocument')}
          </Button>
        }
        hideBackButton
      />

      <div className="flex flex-wrap gap-3">
        <Card className="min-w-[180px] flex-1 gap-2 py-2 sm:gap-6 sm:py-4">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-muted-foreground text-sm">
                  {t('documents:totalDocuments')}
                </p>
                {stats ? (
                  <p className="text-lg font-bold sm:text-2xl">{stats.total}</p>
                ) : (
                  <Skeleton className="mt-1 h-7 w-12" />
                )}
              </div>
              <div className="rounded-lg bg-blue-100 p-1.5 sm:p-2 dark:bg-blue-900/30">
                <FileText className="h-4 w-4 text-blue-600 sm:h-5 sm:w-5" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="min-w-[180px] flex-1 gap-2 py-2 sm:gap-6 sm:py-4">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-muted-foreground text-sm">
                  {t('documents:expiringDocuments')}
                </p>
                {stats ? (
                  <p className="text-lg font-bold sm:text-2xl">
                    {stats.expiringSoon}
                  </p>
                ) : (
                  <Skeleton className="mt-1 h-7 w-12" />
                )}
              </div>
              <div className="rounded-lg bg-yellow-100 p-1.5 sm:p-2 dark:bg-yellow-900/30">
                <AlertCircle className="h-4 w-4 text-yellow-600 sm:h-5 sm:w-5" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="min-w-[180px] flex-1 gap-2 py-2 sm:gap-6 sm:py-4">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-muted-foreground text-sm">
                  {t('documents:expired')}
                </p>
                {stats ? (
                  <p className="text-lg font-bold sm:text-2xl">
                    {stats.expired}
                  </p>
                ) : (
                  <Skeleton className="mt-1 h-7 w-12" />
                )}
              </div>
              <div className="rounded-lg bg-red-100 p-1.5 sm:p-2 dark:bg-red-900/30">
                <AlertCircle className="h-4 w-4 text-red-600 sm:h-5 sm:w-5" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs
        value={entityTab}
        onValueChange={handleTabChange}
        className="w-full"
      >
        <Card className="bg-background max-md:rounded-none max-md:border-0 max-md:bg-transparent max-md:shadow-none">
          <CardContent className="space-y-4 px-4 max-md:px-0">
            <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
              <TabsList className="flex h-auto w-full justify-start gap-1 overflow-x-auto p-1 sm:w-auto sm:flex-wrap sm:justify-center sm:overflow-visible">
                <TabsTrigger value="all" className="shrink-0 text-xs">
                  {t('documents:all')} ({stats?.total ?? 0})
                </TabsTrigger>
                <TabsTrigger
                  value={DocumentEntityType.Vehicle}
                  className="shrink-0 text-xs"
                >
                  {t('documents:vehicles')} ({stats?.byEntityType.vehicle ?? 0})
                </TabsTrigger>
                <TabsTrigger
                  value={DocumentEntityType.Driver}
                  className="shrink-0 text-xs"
                >
                  {t('documents:drivers')} ({stats?.byEntityType.driver ?? 0})
                </TabsTrigger>
                <TabsTrigger
                  value={DocumentEntityType.Route}
                  className="shrink-0 text-xs"
                >
                  {t('documents:routes')} ({stats?.byEntityType.route ?? 0})
                </TabsTrigger>
                <TabsTrigger
                  value={DocumentEntityType.Organization}
                  className="shrink-0 text-xs"
                >
                  {t('documents:general')} (
                  {stats?.byEntityType.organization ?? 0})
                </TabsTrigger>
              </TabsList>

              <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center">
                {isFetching && (
                  <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                    <Spinner className="size-4" />
                    <span>{t('common:refreshing')}</span>
                  </div>
                )}

                <div className="hidden sm:block">
                  <Tabs
                    value={layout}
                    onValueChange={(v) => handleLayoutChange(v as LayoutView)}
                  >
                    <TabsList variant="icon">
                      <TabsTrigger
                        value="cards"
                        aria-label={t('common:layout.cards')}
                        variant="icon"
                      >
                        <LayoutGrid className="size-4" />
                      </TabsTrigger>
                      <TabsTrigger
                        value="table"
                        aria-label={t('common:layout.table')}
                        variant="icon"
                      >
                        <Rows3 className="size-4" />
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>

                <Select value={expiryFilter} onValueChange={handleExpiryChange}>
                  <SelectTrigger className="h-10 w-full sm:w-[150px]">
                    <SelectValue placeholder={t('documents:all')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('documents:all')}</SelectItem>
                    <SelectItem value={DocumentExpiryFilter.Valid}>
                      {t('documents:valid')}
                    </SelectItem>
                    <SelectItem value={DocumentExpiryFilter.ExpiringSoon}>
                      {t('documents:expiringSoon')}
                    </SelectItem>
                    <SelectItem value={DocumentExpiryFilter.Expired}>
                      {t('documents:expired')}
                    </SelectItem>
                  </SelectContent>
                </Select>

                <div className="relative w-full sm:w-auto sm:min-w-[220px]">
                  <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                  <Input
                    placeholder={t('documents:searchDocuments')}
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="h-10 pr-9 pl-9 placeholder:text-sm"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => handleSearchChange('')}
                      aria-label={t('documents:clearSearch')}
                      className="text-muted-foreground hover:bg-muted hover:text-foreground absolute top-1/2 right-1.5 -translate-y-1/2 rounded-md p-1 transition-colors"
                    >
                      <X className="size-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {layout === 'cards' ? (
              <DocumentGrid
                docs={sortedItems}
                onCardClick={setSelectedDocument}
                onReplace={setDocumentToReplace}
                onDelete={setDocumentToDelete}
                deletingDocId={deletingDocId}
              />
            ) : (
              <DocumentTable
                docs={sortedItems}
                sort={sort}
                onSort={handleSort}
                onRowClick={setSelectedDocument}
                onReplace={setDocumentToReplace}
                onDelete={setDocumentToDelete}
                deletingDocId={deletingDocId}
              />
            )}

            {paginated && paginated.totalCount > 0 && (
              <ListPagination
                page={paginated.page}
                totalPages={paginated.totalPages}
                pageSize={paginated.pageSize}
                totalCount={paginated.totalCount}
                hasPrevious={paginated.hasPrevious}
                hasNext={paginated.hasNext}
                onPageChange={setPage}
                onPageSizeChange={handlePageSizeChange}
              />
            )}
          </CardContent>
        </Card>
      </Tabs>

      <CreateDocumentDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />

      {selectedDocument && (
        <DocumentDetailsDialog
          document={selectedDocument}
          onOpenChange={() => setSelectedDocument(null)}
          onReplace={(doc) => {
            setSelectedDocument(null)
            setDocumentToReplace(doc)
          }}
          onDelete={(doc) => {
            setSelectedDocument(null)
            setDocumentToDelete(doc)
          }}
        />
      )}

      {documentToReplace && (
        <ReplaceDocumentDialog
          document={documentToReplace}
          onOpenChange={() => setDocumentToReplace(null)}
        />
      )}

      <ConfirmDeleteDialog
        open={documentToDelete !== null}
        onOpenChange={(open) => {
          if (!open) setDocumentToDelete(null)
        }}
        onConfirm={() => {
          if (!documentToDelete) return
          setDocumentToDelete(null)
          deleteDocument(documentToDelete.id)
        }}
        title={t('documents:delete')}
        description={t('documents:deleteConfirmDescription')}
        cancelLabel={t('common:cancel')}
        confirmLabel={t('documents:delete')}
      />
    </div>
  )
}
